import { useEffect } from 'react';
import { useQuery, ApolloError } from '@apollo/client';
import get from 'lodash/get';
import { Filters } from 'types/generated/graphql';
import { ControllerQueryConfig, UpdateResultGeneric } from '../../types';

const useGenericSearchQuery = <I>({
    queryConfig,
    queryString,
    page,
    itemsPerPage,
    updateResult,
    updateLoading,
    updateError,
    updateCompleted,
}: {
    queryConfig: ControllerQueryConfig;
    queryString: string;
    page: number;
    itemsPerPage: number;
    updateResult: UpdateResultGeneric<I>;
    updateLoading: (loading: boolean) => void;
    updateError: (error: ApolloError) => void;
    updateCompleted: () => void;
}) => {
    const { loading, data } = useQuery(queryConfig?.query, {
        skip: !!queryConfig?.skip,
        variables: {
            take: itemsPerPage,
            page: page,
            query: queryString,
            offset: 0,
            ...queryConfig?.variables,
        },
        onError: (error) => {
            updateError(error);
        },
        onCompleted: updateCompleted,
    });

    useEffect(() => {
        if (data) {
            const pathToItems = `${queryConfig.pathToItems}.data`;
            const pathToTotalItems = `${queryConfig.pathToItems}.meta[${queryConfig.pathToTotalItems}]`;
            const pathToFilters = `${queryConfig.pathToItems}.meta.filters`;

            const items = get<I[]>(data, pathToItems, []);
            const total = get<number | undefined>(
                data,
                pathToTotalItems,
                undefined,
            );
            const filterMetaObj = get<Filters | undefined>(
                data,
                pathToFilters,
                undefined,
            );

            updateResult({
                items,
                total,
                filterMetaObj,
            });
        }
    }, [
        data,
        updateResult,
        queryConfig.pathToTotalItems,
        queryConfig.pathToItems,
    ]);

    useEffect(() => {
        updateLoading(loading);
    }, [loading, updateLoading]);
};

export default useGenericSearchQuery;
